import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, Globe } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { useSiteData } from '@/hooks/useSiteData';
import { clearShopScrollState, clearHomeScrollState } from '@/utils/scrollStorage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { CartSidebar } from './CartSidebar';
import { SearchDropdown } from './SearchDropdown';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const { itemCount } = useCart();
  const location = useLocation();
  
  // Single aggregated query for banner + logo
  const { data: siteData } = useSiteData();
  const banner = siteData?.banner;
  const headerLogo = siteData?.headerLogoUrl;

  // Use the new logo for all cases
  const logoSrc = headerLogo?.includes('/images/logo.png')
    ? '/images/caivonclub-logo.png'
    : (headerLogo || '/images/caivonclub-logo.png');

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    clearHomeScrollState();
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavClick = (href: string) => {
    if (href === '/') {
      clearHomeScrollState();
    } else if (href === '/shop') {
      clearShopScrollState();
    }
  };


  const navigation = [
    { name: t('home'), href: '/' },
    { name: t('shop'), href: '/shop' },
    { name: t('about'), href: '/about' },
    { name: t('contact'), href: '/contact' },
    { name: t('trackYourOrder'), href: '/track-order' },
  ];

  const getBannerText = () => {
    if (!banner) return '';
    if (language === 'sv' && banner.text_sv) return banner.text_sv;
    if (language === 'en' && banner.text_en) return banner.text_en;
    if (language === 'ar' && banner.text_ar) return banner.text_ar;
    return banner.text || banner.text_en || '';
  };

  return (
    <>
      {/* Animated Banner */}
      {banner && (
        <div 
          className="bg-primary text-primary-foreground py-2 overflow-hidden relative"
          dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
          <div 
            className={`whitespace-nowrap inline-block animate-marquee ${language === 'ar' ? 'text-arabic' : ''}`}
            style={{
              animationDuration: `${banner.animation_speed || 30}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              unicodeBidi: 'plaintext',
              textRendering: 'optimizeLegibility'
            }}
          >
            <span className="text-sm px-4" style={{ unicodeBidi: 'plaintext' }}>{getBannerText()}</span>
            <span className="text-sm px-4" style={{ unicodeBidi: 'plaintext' }}>{getBannerText()}</span>
            <span className="text-sm px-4" style={{ unicodeBidi: 'plaintext' }}>{getBannerText()}</span>
            <span className="text-sm px-4" style={{ unicodeBidi: 'plaintext' }}>{getBannerText()}</span>
          </div>
        </div>
      )}
      
      <header dir="ltr" className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b border-border shadow-soft">
        <nav className="section-container py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2">
              {logoSrc && (
                <img 
                  src={logoSrc} 
                  alt="Càivonclub - Luxury organic beauty from Lebanon"
                  className="h-10 w-10 object-contain"
                  width="40"
                  height="40"
                  loading="eager"
                  decoding="async"
                />
              )}
              <div className="text-2xl md:text-3xl font-serif font-semibold text-foreground">
                Càivonclub
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className="text-sm font-medium text-foreground/80 hover:text-foreground smooth-transition relative group"
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full smooth-transition" />
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <SearchDropdown />

              {/* Language Switcher - Desktop only */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative hidden md:flex"
                    aria-label={t('changeLanguage')}
                  >
                    <Globe className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setLanguage('sv')}>
                    Svenska {language === 'sv' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('en')}>
                    English {language === 'en' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('ar')}>
                    العربية {language === 'ar' && '✓'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setCartOpen(true)}
                aria-label={`${t('openCart')}${itemCount > 0 ? ` (${itemCount})` : ''}`}
              >
                <ShoppingCart className="h-5 w-5" aria-hidden="true" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center" aria-hidden="true">
                    {itemCount}
                  </span>
                )}
              </Button>

              {/* Mobile Menu Toggle */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" aria-label={t('openMenu')}>
                    <Menu className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side={language === 'ar' ? 'left' : 'right'} className="w-[300px] p-0">
                  <div className="flex flex-col h-full">
                    {/* Menu Header */}
                    <div className="px-6 py-8 border-b border-border/50">
                      <span className="text-sm font-medium tracking-widest uppercase text-muted-foreground">Menu</span>
                    </div>
                    
                    {/* Navigation Links */}
                    <nav className="flex-1 px-6 py-6">
                      <ul className="space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              onClick={() => {
                                handleNavClick(item.href);
                                setMobileMenuOpen(false);
                              }}
                              className="group flex items-center py-3 text-lg font-medium text-foreground/80 hover:text-foreground transition-colors"
                            >
                              <span className="relative">
                                {item.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300" />
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </nav>
                    
                    {/* Language Switcher Footer */}
                    <div className="mt-auto px-6 py-6 border-t border-border/50 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setLanguage('sv'); setMobileMenuOpen(false); }}
                            className={`px-3 py-1.5 rounded text-xs font-semibold tracking-wide transition-all ${
                              language === 'sv' 
                                ? 'bg-primary text-primary-foreground shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            SV
                          </button>
                          <button
                            onClick={() => { setLanguage('en'); setMobileMenuOpen(false); }}
                            className={`px-3 py-1.5 rounded text-xs font-semibold tracking-wide transition-all ${
                              language === 'en' 
                                ? 'bg-primary text-primary-foreground shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            EN
                          </button>
                          <button
                            onClick={() => { setLanguage('ar'); setMobileMenuOpen(false); }}
                            className={`px-3 py-1.5 rounded text-xs font-semibold tracking-wide transition-all ${
                              language === 'ar' 
                                ? 'bg-primary text-primary-foreground shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            AR
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </nav>
      </header>

      <CartSidebar open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};